/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {enableProdMode} from '@angular/core';
import {platformBrowserDynamic} from '@angular/platform-browser-dynamic';
import {AppModule} from './app/app.module';
import {EnvironmentConfig} from '@shared/models/environment-config';
import {environment} from '@paas-environments/environment';
import {APP_CONFIG, isProduction, isSandbox} from '@common-config/app-config';
import * as Sentry from '@sentry/angular';

(async () => {
  /**
   * Enable the application to dynamically read the environment variables from the server before starting up.
   * This allows one build for any environment, and they can be switched and the application will still
   * not require an additional build for that environment.
   */
  const configFile: EnvironmentConfig = await fetch('./assets/data/app-config.json').then(
    data => data.json()
  );
  /**
   * Init Sentry
   */
  Sentry.init({
    environment: configFile.cloudEnvironment,
    dsn: configFile.sentryDsn,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', configFile.apiBaseUrl],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  if (environment.production || isSandbox(configFile) || isProduction(configFile)) {
    enableProdMode();
  }
  /**
   * Pass the fetched app configuration as an "APP_CONFIG" InjectionToken which the application can then use when needed
   */
  platformBrowserDynamic([{provide: APP_CONFIG, useValue: configFile}])
    .bootstrapModule(AppModule)
    .catch(err => Sentry.captureException(err));
})();
